/**
 * DocuIdApiService — document and share operations via the oRPC client.
 *
 * All calls go through `addinClient` which uses RPCLink pointing at the
 * DocuID web app's /api/rpc endpoint. Types are inferred end-to-end from
 * the AppRouter defined in packages/api.
 *
 * Field mapping notes:
 *  - oRPC documents use `fileName`, `mimeType`, `fileSizeBytes` (DB column names)
 *  - Legacy add-in UI uses `fileName`, `fileType`, `fileSize` — mapped below
 *  - Shares in the new API are encrypted; the add-in uses createLink for public links
 */

import { generateLinkKey, unwrapDek, wrapDekWithLinkKey } from '@docuid/crypto-client';

import type {
  AddinShareRequest,
  ApiResponse,
  DocuIdDocument,
  DocumentAccess,
  ShareApiResponse,
} from '../common/types';
import { addinClient, UNAUTHORIZED_EVENT } from './addinClient';
import { AuthService } from './AuthService';
import { getDashboardBaseUrl } from './config';
import { logger } from './Logger';
import type { OfficeHost } from './OfficeHostService';

const apiLogger = logger.createContextLogger('DocuIdApiService');

// Map Office host → MIME prefix for documents.list filters.mimeType.
// Server matches as a prefix (LIKE 'prefix%'), so full types such as
// ...wordprocessingml.document still match these family prefixes.
const HOST_MIME_PREFIX: Record<OfficeHost, string> = {
  Word: 'application/vnd.openxmlformats-officedocument.wordprocessingml',
  Excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml',
  PowerPoint: 'application/vnd.openxmlformats-officedocument.presentationml',
  Unknown: 'application/vnd.openxmlformats-officedocument.wordprocessingml',
};

export class DocuIdApiService {
  /**
   * List documents for the current Office host, filtered by MIME type.
   */
  static async getDocuments(host: OfficeHost = 'Word'): Promise<DocuIdDocument[]> {
    if (AuthService.isDemoMode()) {
      apiLogger.info(`Returning demo documents for host: ${host}`);
      return DocuIdApiService.getDemoDocuments(host);
    }
    const mimePrefix = HOST_MIME_PREFIX[host] ?? HOST_MIME_PREFIX.Word;
    apiLogger.info(`Fetching documents for host: ${host}`);

    try {
      const result = await addinClient.documents.list({
        filters: { mimeType: mimePrefix },
      });

      // Map the oRPC document entity to the legacy DocuIdDocument shape
      // so existing UI components don't need to change.
      return result.items.map((document) => ({
        documentId: 0, // UUID-based system; use as identifier only via `id` field
        fileName: document.fileName,
        filePath: document.storageKey,
        fileType: document.mimeType,
        fileSize: document.fileSizeBytes,
        description: document.description ?? null,
        folderId: null, // folderId is UUID in new system; not used in add-in UI
        isPasswordProtected: document.isPasswordProtected,
        createdAt:
          document.createdAt instanceof Date
            ? document.createdAt.toISOString()
            : String(document.createdAt),
        updatedAt:
          document.updatedAt instanceof Date
            ? document.updatedAt.toISOString()
            : String(document.updatedAt),
        // Pass through the UUID so DocumentService can reference it
        _uuid: document.id,
      }));
    } catch (error) {
      apiLogger.error('Error fetching documents', error as Error);
      throw error;
    }
  }

  /**
   * Get document metadata. In the new system, the document entity itself
   * contains metadata; download access is obtained through the share-access flow.
   */
  static async getDocumentAccess(documentId: number | string): Promise<DocumentAccess> {
    if (AuthService.isDemoMode()) {
      apiLogger.info(`Returning demo document access for doc: ${documentId}`);
      const idString = String(documentId);

      let fileName = 'Demo Proposal.docx';
      let fileType = 'text/plain';
      let fileSize = 1_024_500;
      let description = 'Demo Word document';
      let isPasswordProtected = false;
      let accessUrl = 'demo-word-content-url';

      switch (idString) {
        case 'demo-doc-excel-1':
        case '2001': {
          fileName = 'Demo Budget.xlsx';
          fileType = 'text/csv';
          fileSize = 512_000;
          description = 'Demo Excel document';
          accessUrl = 'demo-excel-content-url';

          break;
        }
        case 'demo-doc-excel-2':
        case '2002': {
          fileName = 'Quarterly Data.xlsx';
          fileType = 'text/csv';
          fileSize = 1_536_000;
          description = 'Demo Excel document';
          isPasswordProtected = true;
          accessUrl = 'demo-excel-content-url';

          break;
        }
        case 'demo-doc-ppt-1':
        case '3001': {
          fileName = 'Demo Presentation.pptx';
          fileType = 'text/plain';
          fileSize = 2_048_000;
          description = 'Demo PowerPoint document';
          accessUrl = 'demo-ppt-presentation-url';

          break;
        }
        case 'demo-doc-ppt-2':
        case '3002': {
          fileName = 'Investor Deck.pptx';
          fileType = 'text/plain';
          fileSize = 4_096_000;
          description = 'Demo PowerPoint document';
          isPasswordProtected = true;
          accessUrl = 'demo-ppt-investor-url';

          break;
        }
        case 'demo-doc-word-2':
        case '1002': {
          fileName = 'Confidential Report.docx';
          fileType = 'text/plain';
          fileSize = 2_048_000;
          description = 'Demo Word document';
          isPasswordProtected = true;
          accessUrl = 'demo-word-content-url';

          break;
        }
        // No default
      }

      return {
        documentId: 0,
        fileName,
        fileType,
        fileSize,
        description,
        folderId: null,
        isPasswordProtected,
        isEncrypted: false,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        access: {
          url: accessUrl,
          previewUrl: accessUrl,
          expiresIn: 3600,
          method: 'GET',
          headers: {},
        },
      };
    }

    apiLogger.info(`Getting document: ${documentId}`);

    try {
      const document = await addinClient.documents.get({ id: String(documentId) });

      return {
        documentId: 0,
        fileName: document.fileName,
        fileType: document.mimeType,
        fileSize: document.fileSizeBytes,
        description: document.description ?? null,
        folderId: null,
        isPasswordProtected: document.isPasswordProtected,
        isEncrypted: true, // Files are encrypted in monorepo S3 storage
        status: document.status,
        createdAt:
          document.createdAt instanceof Date
            ? document.createdAt.toISOString()
            : String(document.createdAt),
        updatedAt:
          document.updatedAt instanceof Date
            ? document.updatedAt.toISOString()
            : String(document.updatedAt),
        access: {
          url: `${getDashboardBaseUrl()}/api/files/${document.id}/content`,
          previewUrl: null,
          expiresIn: null,
          method: 'GET',
          headers: {},
        },
      };
    } catch (error) {
      apiLogger.error('Error getting document', error as Error);
      throw error;
    }
  }

  /**
   * Download document content as a Blob from a pre-signed URL.
   */
  static async downloadDocumentContent(accessUrl: string): Promise<Blob> {
    if (
      accessUrl === 'demo-word-content-url' ||
      accessUrl === 'demo-excel-content-url' ||
      accessUrl === 'demo-powerpoint-content-url' ||
      accessUrl === 'demo-ppt-presentation-url' ||
      accessUrl === 'demo-ppt-investor-url' ||
      AuthService.isDemoMode()
    ) {
      apiLogger.info(`Returning demo content for URL: ${accessUrl}`);
      return DocuIdApiService.createDemoContent(accessUrl);
    }

    apiLogger.info(`Downloading document content`);

    try {
      const response = await fetch(accessUrl, { credentials: 'include' });
      if (response.status === 401) {
        DocuIdApiService.dispatchUnauthorized();
      }
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      return await response.blob();
    } catch (error) {
      apiLogger.error('Error downloading content', error as Error);
      throw error;
    }
  }

  /**
   * Share a document with a recipient by creating an external share link.
   * Uses shares.createExternal for email/mobile recipients.
   *
   * Note: The new API requires a wrappedDek for encrypted documents.
   * For unencrypted documents, a placeholder empty key is used.
   */
  static async shareDocument(payload: AddinShareRequest): Promise<ApiResponse<ShareApiResponse>> {
    if (AuthService.isDemoMode()) {
      apiLogger.info('Returning demo share response');
      return {
        success: true,
        data: {
          shareId: 999,
          shareLink: 'https://demo.docuid.net/share/demolink',
          message: 'Document shared successfully in demo mode.',
        },
        message: 'Document shared successfully',
      };
    }

    const documentIdString = String(payload.documentId);
    apiLogger.info(`Sharing document: ${documentIdString}`);

    try {
      let wrappedDek = btoa('unencrypted');
      let linkKey: string | undefined;

      const documentKey = await addinClient.encryptionKeys.getDocumentKey({
        documentId: documentIdString,
      });

      if (documentKey && documentKey.model === 'client_rsa') {
        const privateKey = AuthService.getUserPrivateKey();
        if (!privateKey) {
          throw new Error(
            'Session locked or key unavailable. Please log in again to unlock encryption keys.',
          );
        }
        const dek = await unwrapDek(documentKey.wrappedDek, privateKey);
        linkKey = generateLinkKey();
        wrappedDek = await wrapDekWithLinkKey(dek, linkKey);
      }

      const recipientMobile = payload.mobile
        ? payload.countryCode
          ? `+${payload.countryCode.replace(/^\+/, '')}${payload.mobile}`
          : payload.mobile
        : undefined;

      const result = await addinClient.shares.createExternal({
        documentId: documentIdString,
        recipientEmail: payload.email || undefined,
        recipientMobile,
        accessLevel: 'download',
        wrappedDek,
        recipientKeyVersion: linkKey ? 'link-v1' : undefined,
        message: payload.message || undefined,
      });

      const base = getDashboardBaseUrl();
      const shareLink = linkKey
        ? `${base}/share/${result.shareToken}#k=${linkKey}`
        : `${base}/share/${result.shareToken}`;

      return {
        success: true,
        data: {
          shareLink,
          message: 'Document shared successfully',
        },
        message: 'Document shared successfully',
      };
    } catch (error) {
      apiLogger.error('Error sharing document', error as Error);
      throw error;
    }
  }

  /**
   * Retrieve the list of workspaces available to the logged-in user.
   */
  static async listWorkspaces(): Promise<{ id: string; name: string }[]> {
    if (AuthService.isDemoMode()) {
      apiLogger.info('Returning demo workspaces');
      return [{ id: 'demo-workspace-1', name: 'Demo Workspace' }];
    }

    apiLogger.info('Fetching workspaces');
    try {
      return await addinClient.workspaces.list();
    } catch (error) {
      apiLogger.error('Error fetching workspaces', error as Error);
      throw error;
    }
  }

  /**
   * Retrieve the list of folders inside a workspace.
   */
  static async listFolders(workspaceId?: string): Promise<{ id: string; name: string }[]> {
    if (AuthService.isDemoMode()) {
      apiLogger.info('Returning demo folders');
      return [{ id: 'demo-folder-1', name: 'Demo Folder' }];
    }

    apiLogger.info('Fetching folders for workspace', { workspaceId });
    try {
      return await addinClient.folders.list({ workspaceId });
    } catch (error) {
      apiLogger.error('Error fetching folders', error as Error);
      throw error;
    }
  }

  /**
   * Initialize a new document or version upload session.
   */
  static async createUploadSession(parameters: {
    fileName: string;
    size: number;
    mimeType: string;
    workspaceId?: string | null;
    folderId?: string;
    documentId?: string;
    encryption: { fileIv: string; wrappedDek: string };
  }): Promise<{ sessionId: string; uploadUrl: string }> {
    apiLogger.info('Creating upload session', {
      fileName: parameters.fileName,
      documentId: parameters.documentId,
    });
    try {
      return await addinClient.uploads.createSession({
        fileName: parameters.fileName,
        size: parameters.size,
        mimeType: parameters.mimeType,
        workspaceId: parameters.workspaceId ?? null,
        folderId: parameters.folderId,
        documentId: parameters.documentId,
        encryption: parameters.encryption,
      });
    } catch (error) {
      apiLogger.error('Error creating upload session', error as Error);
      throw error;
    }
  }

  /**
   * Finalize the document or version upload session on the server.
   */
  static async completeUploadSession(sessionId: string): Promise<{ id: string }> {
    apiLogger.info('Completing upload session', { sessionId });
    try {
      return await addinClient.uploads.completeSession({ sessionId });
    } catch (error) {
      apiLogger.error('Error completing upload session', error as Error);
      throw error;
    }
  }

  /**
   * Upload the raw ArrayBuffer ciphertext bytes directly to the REST endpoint.
   * Mirrors dashboard `encrypt-and-upload.ts`: session id must be sent in
   * `x-upload-session-id` (see apps/dashboard/app/api/uploads/route.ts).
   */
  static async uploadCiphertextBytes(
    uploadUrl: string,
    sessionId: string,
    bytes: ArrayBuffer,
  ): Promise<void> {
    apiLogger.info('Uploading ciphertext payload', {
      bytesLength: bytes.byteLength,
      sessionId,
    });
    try {
      let finalUrl = uploadUrl;
      if (uploadUrl.startsWith('/')) {
        finalUrl = `${getDashboardBaseUrl()}${uploadUrl}`;
      }

      const response = await fetch(finalUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-upload-session-id': sessionId,
        },
        body: bytes,
        credentials: 'include',
      });

      if (response.status === 401) {
        DocuIdApiService.dispatchUnauthorized();
      }
      if (!response.ok) {
        let detail = `Upload failed with status ${String(response.status)}`;
        try {
          const parsed = (await response.json()) as { error?: string };
          if (parsed.error) {
            detail = parsed.error;
          }
        } catch {
          const errorText = await response.text();
          if (errorText) {
            detail = errorText;
          }
        }
        throw new Error(detail);
      }
    } catch (error) {
      apiLogger.error('Error uploading bytes', error as Error);
      throw error;
    }
  }

  private static dispatchUnauthorized(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }

  private static getDemoDocuments(host: string): DocuIdDocument[] {
    if (host === 'Excel') {
      return [
        {
          documentId: 2001,
          fileName: 'Demo Budget.xlsx',
          filePath: 'demo-excel-1',
          fileType: 'text/csv',
          fileSize: 512_000,
          description: 'A sample budget spreadsheet for demonstration.',
          folderId: null,
          isPasswordProtected: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _uuid: 'demo-doc-excel-1',
        } as unknown as DocuIdDocument,
        {
          documentId: 2002,
          fileName: 'Quarterly Data.xlsx',
          filePath: 'demo-excel-2',
          fileType: 'text/csv',
          fileSize: 1_536_000,
          description: 'A sample quarterly data spreadsheet for demonstration.',
          folderId: null,
          isPasswordProtected: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _uuid: 'demo-doc-excel-2',
        } as unknown as DocuIdDocument,
      ];
    }

    if (host === 'PowerPoint') {
      return [
        {
          documentId: 3001,
          fileName: 'Demo Presentation.pptx',
          filePath: 'demo-ppt-1',
          fileType: 'text/plain',
          fileSize: 2_048_000,
          description: 'A sample presentation for demonstration.',
          folderId: null,
          isPasswordProtected: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _uuid: 'demo-doc-ppt-1',
        } as unknown as DocuIdDocument,
        {
          documentId: 3002,
          fileName: 'Investor Deck.pptx',
          filePath: 'demo-ppt-2',
          fileType: 'text/plain',
          fileSize: 4_096_000,
          description: 'A sample investor deck for demonstration.',
          folderId: null,
          isPasswordProtected: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _uuid: 'demo-doc-ppt-2',
        } as unknown as DocuIdDocument,
      ];
    }

    // Default to Word
    return [
      {
        documentId: 1001,
        fileName: 'Demo Proposal.docx',
        filePath: 'demo-doc-word-1',
        fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        fileSize: 1_024_500,
        description: 'A sample proposal document for demonstration.',
        folderId: null,
        isPasswordProtected: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _uuid: 'demo-doc-word-1',
      } as unknown as DocuIdDocument,
      {
        documentId: 1002,
        fileName: 'Confidential Report.docx',
        filePath: 'demo-doc-word-2',
        fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        fileSize: 2_048_000,
        description: 'A sample confidential report for demonstration.',
        folderId: null,
        isPasswordProtected: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _uuid: 'demo-doc-word-2',
      } as unknown as DocuIdDocument,
    ];
  }

  private static createDemoContent(accessUrl: string): Blob {
    let text: string;
    let mimeType: string;

    switch (accessUrl) {
      case 'demo-excel-content-url': {
        text =
          'Category,Q1,Q2,Q3,Q4,Total\n' +
          'Revenue,120000,135000,148000,162000,565000\n' +
          'Cost of Goods,72000,81000,88800,97200,339000\n' +
          'Gross Profit,48000,54000,59200,64800,226000\n' +
          'Operating Expenses,30000,32000,34000,36000,132000\n' +
          'Net Income,18000,22000,25200,28800,94000';
        mimeType = 'text/csv';

        break;
      }
      case 'demo-ppt-presentation-url':
      case 'demo-powerpoint-content-url': {
        text =
          'iVALT DocuID  |  Product Showcase\n' +
          '\n' +
          'What is DocuID?\n' +
          'DocuID integrates biometric authentication directly into Microsoft Office,\n' +
          'enabling secure, identity-verified document access at enterprise scale.\n' +
          '\n' +
          'Key Features\n' +
          '  - Biometric login via iVALT mobile app\n' +
          '  - One-tap access to Word, Excel and PowerPoint files — no passwords\n' +
          '  - Every open is logged with verified user identity and timestamp\n' +
          '  - Share documents with recipient-verified, granular permissions\n' +
          '\n' +
          'How It Works\n' +
          '  1. Open the DocuID add-in from the Office ribbon\n' +
          '  2. Enter your registered mobile number\n' +
          '  3. Approve the biometric prompt on the iVALT app\n' +
          '  4. Browse and open your secured documents instantly\n' +
          '\n' +
          'Supported Hosts\n' +
          '  Microsoft Word  |  Microsoft Excel  |  Microsoft PowerPoint\n' +
          '\n' +
          'demo.docuid.net  |  support@docuid.net\n' +
          'Generated: ' +
          new Date().toLocaleString();
        mimeType = 'text/plain';

        break;
      }
      case 'demo-ppt-investor-url': {
        text =
          'iVALT DocuID  |  Investor Overview\n' +
          '\n' +
          'The Problem\n' +
          'Traditional document security relies on static passwords and shareable links\n' +
          'that can be forwarded, stolen or forgotten — exposing sensitive data.\n' +
          '\n' +
          'Our Solution\n' +
          'DocuID binds every document open to a verified biometric identity.\n' +
          'Only the intended recipient — confirmed on their own device — can access the file.\n' +
          '\n' +
          'Market Opportunity\n' +
          '  - 1.2 billion+ Microsoft Office users globally\n' +
          '  - $42 billion document security market (2025 estimate)\n' +
          '  - 78% of data breaches involve compromised credentials (Verizon DBIR 2024)\n' +
          '\n' +
          'Business Model\n' +
          '  - SaaS subscription per seat / month\n' +
          '  - Enterprise site licences with SSO integration\n' +
          '  - API access tier for custom workflow integrations\n' +
          '\n' +
          'Traction\n' +
          '  - Active pilot deployments in legal, finance and healthcare sectors\n' +
          '  - Zero credential-based document breaches across all deployments\n' +
          '\n' +
          'Contact\n' +
          '  investors@docuid.net  |  www.docuid.net\n' +
          'Generated: ' +
          new Date().toLocaleString();
        mimeType = 'text/plain';

        break;
      }
      default: {
        text =
          'iVALT DocuID - Demo Document\n\n' +
          'This is a demo document generated for the iVALT DocuID Office Add-in preview.\n\n' +
          'Section 1: Overview\n' +
          'iVALT DocuID provides biometric-secured document management directly inside ' +
          'Microsoft Office applications.\n\n' +
          'Section 2: Features\n' +
          '- One-tap biometric login via the iVALT mobile app\n' +
          '- Access Word, Excel, and PowerPoint files stored in DocuID\n' +
          '- Share documents with granular permission controls\n' +
          '- Full audit trail for every document access\n\n' +
          'All features are unlocked in demo mode.\n' +
          'Document generated on: ' +
          new Date().toLocaleString();
        mimeType = 'text/plain';
      }
    }

    const encoder = new TextEncoder();
    return new Blob([encoder.encode(text)], { type: mimeType });
  }
}
